import {Editor} from "./Editor.js";
import {valueOrDefault} from "../utils/Util.js";
import {Temp} from "../utils/Temp.js";

let uid = 0;

export class ArrayEditor extends Editor {

	/**
	 * @param member 		Must point to an array
	 * @param ItemEditor 	class of item editor
	 * @param options 		this editor's widget options
	 * @param ieOptions 	options for item editor's widget
	 *
	 * Standard options:
	 * 	minSize		minimum length of the array (default 0)
	 * 	maxSize		maximum length of the array (default 1000)
	 * 	sortable	true/false if the user can change order of the items using drag&drop (default is true)
	 * 	addLabel	label for add button
	 * 	addPreviewKey	WebPreview key for add button ("key-help.message")
	 * 	addTop		add button will be placed at the top (not bottom)
	 */
	constructor(parent, fieldPath, obj, member, ItemEditor, options, ieOptions) {
		super(parent, fieldPath, obj, member);
		this.uid = uid++;
		this.ItemEditor = ItemEditor;
		this.options = options;
		this.ieOptions = ieOptions;
	}

	get readOnly() {
		return valueOrDefault(this.options.readOnly, super.readOnly);
	}

	rebuild() {
		this.children = [];
		this.root = this.widget = Temp.tag('div', this.options);
		this.widget.addClass('array-widget');
		this.container = Temp.tag('div', {clazz: 'array-container'});

		let theArray = this.data;
		if (theArray == null) theArray = this.data = [];
		const minSize = valueOrDefault(this.options.minSize, 0);
		const maxSize = valueOrDefault(this.options.maxSize, 1000);
		while (theArray.length < minSize) theArray.push({});

		for (let i = 0; i < Math.min(theArray.length, maxSize); i++) {
			this.children.push(this.addItemEditor(i));
		}
		this.updatePositions();

		this.btnContainer = null;
		if (!this.options.noAddButton) {
			this.btnContainer = this.generateAddButtons();
			if (typeof this.options.buttonHook === 'function') this.options.buttonHook(this.btnContainer);
		}

		if (this.options.addTop) {
			if (this.btnContainer !== null) {
				this.btnContainer.addClass("ac-top");
				this.widget.append(this.btnContainer);
			}
			this.widget.append(this.container);
		} else {
			this.widget.append(this.container);
			if (this.btnContainer !== null) {
				this.btnContainer.addClass("ac-bottom");
				this.widget.append(this.btnContainer);
			}
		}
		if (this.readOnly && this.btnContainer !== null) this.btnContainer.hide();

		if (this.canSort()) {
			const updateFunc = function (e, ui) {
				const arr = this.data;
				const oldIdx = parseInt(ui.item.attr('data-idx'));
				const newIdx = this.findIdx(oldIdx);
				if (newIdx >= 0 && oldIdx !== newIdx) {
					// There was a move
					let moved = arr[oldIdx];
					arr.splice(oldIdx, 1);
					arr.splice(newIdx, 0, moved);
					moved = this.children[oldIdx];
					this.children.splice(oldIdx, 1);
					this.children.splice(newIdx, 0, moved);
					this.updatePositions();
					this.wasChange(arr);
				}
			}.bind(this);
			this.container.sortable({
				axis: 'y',
				handle: '.array-handle',
				update: updateFunc
			});
			if (this.readOnly) this.container.sortable("disable");
		}
	}

	enableSelf(enable) {
		super.enableSelf(enable);
		if (enable) {
			if (this.btnContainer) this.btnContainer.show();
			if (this.canSort()) this.container.sortable("enable");
		} else {
			if (this.btnContainer) this.btnContainer.hide();
			if (this.canSort()) this.container.sortable("disable");
		}
		this.options.readOnly = !enable;
	}

	generateAddButtons() {
		let btnContainer = $('<div class="array-controls"></div>');
		if (Array.isArray(this.options.addButtons)) {
			let idx = 0;
			for (let btnSpec of this.options.addButtons) {
				this.appendButton(btnContainer, btnSpec, idx++);
			}
		} else {
			this.appendButton(btnContainer, this.options, 0);	// only one button directly in options
		}
		return btnContainer;
	}

	appendButton(container, spec, idx) {
		const button = $('<button class="fbutton" style="cursor: copy" type="button"></button>');
		if (idx > 0) button.css("margin-left", "var(--gap-s)");
		const addLabel = valueOrDefault(spec.addLabel, '+');
		button.append(document.createTextNode(addLabel));
		if (spec.addPreviewKey) button.attr("data-preview", spec.addPreviewKey);
		button.on('click', function (e) {
			const dt = this.data;
			if (e.shiftKey) {
			} else if (dt.length < valueOrDefault(this.options.maxSize, 1000)) {
				const idx = this.options.addTop ? 0 : dt.length;
				let newItem = {};
				if (spec.addTemplate) {
					newItem = spec.addTemplate;
					if (typeof newItem === 'object') newItem = $.extend(true, {}, newItem);	// deep copy
				}

				if (this.options.addTop) dt.unshift(newItem);
				else dt.push(newItem);

				const theNew = this.addItemEditor(idx);
				this.wasChange(dt);

				if (this.options.addTop) this.children.unshift(theNew);
				else this.children.push(theNew);
				this.updatePositions();
				theNew.focus();
			}
		}.bind(this));
		container.append(button);
	}

	canSort() {
		return (this.options.sortable == null || this.options.sortable);
	}

	addItemEditor(idx, appendAfterThis) {
		const rowContainer = $('<div class="array-item-row" data-idx="' + idx + '"></div>');
		rowContainer.addClass("air-"+this.uid);
		const itemContainer = $('<div class="array-item"></div>');
		const ops = this.ieOptions || {};
		const item = new this.ItemEditor(this, this.field + "." + idx, this.data, idx, ops);
		item.inArray = true;
		item.arrayRow = rowContainer;	// this allows subeditor to determine index using data-idx attribute
		itemContainer.append(item.ui);
		rowContainer.append(itemContainer);
		if (this.canSort()) {
			const rowHandler = $('<span class="fi array-handle">&#xe14D;</span>');
			rowContainer.append(rowHandler);
		}
		if (appendAfterThis) appendAfterThis.after(rowContainer);
		else if (idx === 0) this.container.prepend(rowContainer);
		else this.container.append(rowContainer);
		return item;
	}

	updatePositions() {
		let idx = 0;
		this.container.find(".air-"+this.uid).each(function () {
			$(this).attr('data-idx', idx++);
		});
		idx = 0;
		for (let ed of this.children) {
			ed.rename(this.field+"."+idx);
			ed.member = idx++;	// make editor point to the right index
		}
	}

	/**
	 * Insert a new editor after the one specified by arrayRow parameter
	 */
	insertAfter(arrayRow) {
		const myIdx = ArrayEditor.arrayRowIndex(arrayRow);
		const arr = this.data;
		// Insert object into array
		arr.splice(myIdx+1, 0, {});
		const newEd = this.addItemEditor(myIdx + 1, arrayRow);
		this.children.splice(myIdx + 1, 0, newEd);
		this.updatePositions();
		this.wasChange(arr);
		newEd.focus();
		return newEd;
	}

	removeItem(arrayRow) {
		const myIdx = ArrayEditor.arrayRowIndex(arrayRow);
		const arr = this.data;
		// Remove object from array
		arr.splice(myIdx, 1);
		this.children.splice(myIdx, 1);
		arrayRow.detach();
		this.updatePositions();
		this.wasChange(arr);
		let focusIdx = myIdx - 1;
		if (focusIdx < 0) focusIdx = 0;
		this.focus(focusIdx);
	}

	findIdx(dataIdx) {
		const rows = this.container.find(".air-"+this.uid);
		let found = -1;
		for (let i = 0; rows.length; i++) {
			let idx = parseInt(rows.eq(i).attr('data-idx'));
			if (idx === dataIdx) {
				found = i;
				break;
			}
		}
		return found;
	}

	findRow(idx) {
		const rows = this.container.find(".air-"+this.uid);
		if (rows.length <= idx) return null;
		return rows.eq(idx);
	}

	static arrayRowIndex(arrayRow) {
		return parseInt(arrayRow.attr('data-idx'));
	}

	focus(idx) {
		if (idx == null) idx = 0;
		if (idx < this.data.length) {
			this.children[idx].focus();
		}
	}
}
